import React, { useState } from 'react'
import styled from 'styled-components'

import { BulbIcon, Button, useHapticFeedback } from '@blade/ui-kit'
import { useTranslation } from '@root/shared/localization'

import { Drawer } from '../drawer'

import type { IconProps } from '@blade/ui-kit'
import type { ReactElement } from 'react'

type Props = {
  title: React.ReactNode
  content: React.ReactNode
  icon?: ReactElement<IconProps> | undefined
  buttonText?: string | undefined

  children: React.ReactElement<{ onClick?: () => void }>
}

export const TooltipDrawer: React.FC<Props> = ({
  content,
  title,
  icon = <BulbIcon />,
  children,
  buttonText,
}) => {
  const haptics = useHapticFeedback()
  const [open, setOpen] = useState(false)

  const { t } = useTranslation()

  const triggerEl = React.cloneElement(children, {
    onClick: () => {
      haptics.impactOccurred('light')
      setOpen(true)
    },
  })

  return (
    <>
      {triggerEl}
      <Drawer open={open} onOpenChange={setOpen}>
        <Root>
          <IconWrapper>{React.cloneElement(icon, { size: 32 })}</IconWrapper>
          <Title>{title}</Title>
          <Text>{content}</Text>
          <Button
            onClick={() => setOpen(false)}
            fullWidth
            size="m"
            color="primary"
          >
            {buttonText ?? t('common.close')}
          </Button>
        </Root>
      </Drawer>
    </>
  )
}

const Root = styled.div`
  padding-inline: 12px;
  padding-block: 0 var(--safe-bottom);

  display: grid;
  justify-items: center;
  justify-content: center;
`

const IconWrapper = styled.div`
  background: rgba(255, 255, 255, 0.04);

  display: grid;
  width: 58px;
  height: 60px;
  padding: 14px 13px;
  gap: 10px;
  border-radius: 16px;
  justify-self: center;
  margin: 15px 0;
`

const Text = styled.div`
  color: var(--text-10);

  text-align: center;

  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  margin-bottom: 20px;
  padding-inline: 13px;
`

const Title = styled.div`
  color: var(--text-12);
  text-align: center;

  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  margin-bottom: 15px;
  padding-inline: 13px;
`
